
// src/services/forumService.ts
import {
  getFirestore,
  collection,
  query,
  orderBy,
  addDoc,
  onSnapshot,
  deleteDoc,
  doc,
  serverTimestamp,
  getDocs,
  updateDoc,
  getDoc,
  arrayRemove,
  arrayUnion,
  Unsubscribe,
  setDoc,
} from "firebase/firestore";

import { loadUserProfile } from "./storageService";

const db = getFirestore();

interface RawTopic {
  id: string;
  title: string;
  content?: string;
  authorId: string;
  createdAt?: any;
  editedAt?: any;
}

interface Topic extends RawTopic {
  authorName: string;
}

// ---- Forums & Topics API ----

export interface Forum {
  id: string;
  name: string;
  authorId: string;
  tags: string[];
  creatorId: string;
  createdAt?: any;
}

export interface ForumMessage {
  id: string;
  text: string;
  authorId: string;
  authorName: string;
  avatarUrl?: string | null;
  parentId?: string;
  timestamp: any;
  stars?: string[];
  replies?: ForumMessage[]; // For nesting
}

export async function fetchForums(): Promise<Forum[]> {
  const snap = await getDocs(collection(db, "forums"));
  return snap.docs.map((d) => {
    const data = d.data() as any;
    return {
      id: d.id,
      name: data.name,
      authorId: data.authorId,
      tags: Array.isArray(data.tags) ? data.tags : [],
      createdAt: data.createdAt,
      creatorId: data.creatorId || data.authorId, // fallback to authorId if creatorId is not present
    };
  });
}

// Keeping this for reference but not using it
export async function createForum1(
  name: string,
  authorId: string,
  tags: string[] = []
): Promise<string> {
  const ref = await addDoc(collection(db, "forums"), {
    name,
    authorId,
    tags,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

// Fixed createForum function to avoid duplication
export async function createForum(
  name: string,
  userId: string,
  tags: string[]
) {
  // Create a single document reference for the forum
  const forumRef = doc(collection(db, "forums"));
  
  // Ensure tags is a proper array and deduplicate it
  const uniqueTags = Array.from(new Set(tags.filter(tag => tag.trim() !== '')));
  
  // Set the document data with a single write operation
  await setDoc(forumRef, {
    id: forumRef.id,
    name,
    tags: uniqueTags,
    creatorId: userId,
    authorId: userId, // Add for backwards compatibility
    createdAt: serverTimestamp(),
  });
  
  // Return the ID for potential use by the caller
  return forumRef.id;
}

export function subscribeTopics(
  forumId: string,
  callback: (topics: Topic[]) => void
): () => void {
  const q = query(
    collection(db, "forums", forumId, "topics"),
    orderBy("createdAt", "asc")
  );

  const unsub = onSnapshot(q, (snap) => {
    // 1) Assemble raw topics
    const raws: RawTopic[] = snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as any),
    }));

    // 2) Gather unique author IDs
    const authorIds = Array.from(new Set(raws.map((t) => t.authorId)));

    // 3) Fetch all those users in parallel
    Promise.all(
      authorIds.map((uid) =>
        getDoc(doc(db, "users", uid)).then((ud) => ({
          uid,
          displayName: ud.exists() ? (ud.data() as any).displayName : uid,
        }))
      )
    )
      .then((users) => {
        const nameMap = users.reduce<Record<string, string>>((m, u) => {
          m[u.uid] = u.displayName;
          return m;
        }, {});

        // 4) Merge into the final Topic[]
        const topics: Topic[] = raws.map((t) => ({
          ...t,
          authorName: nameMap[t.authorId] || t.authorId,
        }));

        callback(topics);
      })
      .catch((err) => {
        console.error("Error loading topic authors:", err);
        // fallback: feed raw but with authorName = authorId
        callback(raws.map((t) => ({ ...t, authorName: t.authorId })));
      });
  });

  return unsub;
}

export async function createTopic(
  forumId: string,
  title: string,
  content: string,
  authorId: string
): Promise<string> {
  const ref = await addDoc(collection(db, "forums", forumId, "topics"), {
    title,
    content,
    authorId,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function editTopic(
  forumId: string,
  topicId: string,
  updates: { title?: string; content?: string }
): Promise<void> {
  const topicRef = doc(db, "forums", forumId, "topics", topicId);
  await updateDoc(topicRef, {
    ...updates,
    editedAt: serverTimestamp(),
  });
}

export async function deleteTopic(
  forumId: string,
  topicId: string
): Promise<void> {
  await deleteDoc(doc(db, "forums", forumId, "topics", topicId));
}

// ---- Messages API ----

interface RawMessage {
  id: string;
  text: string;
  authorId: string;
  timestamp: any;
  editedAt?: any;
}

interface Message extends RawMessage {
  authorName: string;
}

export function subscribeMessages2(
  forumId: string,
  topicId: string,
  callback: (msgs: Message[]) => void
): () => void {
  const q = query(
    collection(db, "forums", forumId, "topics", topicId, "messages"),
    orderBy("timestamp", "asc")
  );

  const unsub = onSnapshot(q, (snap) => {
    const raws: RawMessage[] = snap.docs.map((d) => ({
      id: d.id,
      ...(d.data() as any),
    }));

    const authorIds = Array.from(
      new Set(
        raws
          .map((m) => m.authorId)
          // filter out any falsey or non-string IDs
          .filter(
            (uid): uid is string => typeof uid === "string" && uid.length > 0
          )
      )
    );

    Promise.all(
      authorIds.map((uid) =>
        getDoc(doc(db, "users", uid))
          .then((ud) => ({
            uid,
            displayName: ud.exists()
              ? (ud.data() as any).displayName
              : "Unknown",
          }))
          .catch((err) => {
            console.warn("Failed to load user", uid, err);
            return { uid, displayName: "Unknown" };
          })
      )
    )
      .then((users) => {
        const nameMap = users.reduce<Record<string, string>>((m, u) => {
          m[u.uid] = u.displayName;
          return m;
        }, {});

        const msgs: Message[] = raws.map((m) => ({
          ...m,
          authorName: nameMap[m.authorId] || m.authorId,
        }));
        callback(msgs);
      })
      .catch((err) => {
        console.error("Error loading message authors:", err);
        callback(raws.map((m) => ({ ...m, authorName: m.authorId })));
      });
  });

  return unsub;
}

// in forumService.ts
export function subscribeMessages1(
  forumId: string,
  topicId: string,
  callback: (msgs: any[]) => void
): () => void {
  const q = query(
    collection(db, "forums", forumId, "topics", topicId, "messages"),
    orderBy("timestamp", "asc")
  );

  return onSnapshot(q, async (snap) => {
    const msgs: any[] = await Promise.all(
      snap.docs.map(async (d) => {
        const data = d.data() as any;
        // look up the author's displayName
        const userSnap = await getDoc(doc(db, "users", data.authorId));
        return {
          id: d.id,
          ...data,
          authorName: userSnap.exists()
            ? (userSnap.data() as any).displayName
            : "Anonymous",
        };
      })
    );
    callback(msgs);
  });
}

export function subscribeMessagesWorking(
  forumId: string,
  topicId: string,
  callback: (
    msgs: {
      id: string;
      text: string;
      authorId: string;
      authorName: string;
      timestamp: any;
      editedAt?: any;
    }[]
  ) => void
): () => void {
  const q = query(
    collection(db, "forums", forumId, "topics", topicId, "messages"),
    orderBy("timestamp", "asc")
  );
  return onSnapshot(q, (snap) => {
    const messages = snap.docs.map((d) => {
      const data = d.data() as any;
      return {
        id: d.id,
        text: data.text,
        authorId: data.authorId,
        authorName: data.authorName,
        timestamp: data.timestamp,
        editedAt: data.editedAt,
      };
    });
    callback(messages);
  });
}

// Subscribe (unchanged except we pass through an optional html field)
export function subscribeMessages(
  forumId: string,
  topicId: string,
  callback: (
    msgs: {
      id: string;
      text: string;
      authorId: string;
      authorName: string;
      timestamp: any;
      editedAt?: any;
      html?: string;
    }[]
  ) => void
): () => void {
  const q = query(
    collection(db, "forums", forumId, "topics", topicId, "messages"),
    orderBy("timestamp", "asc")
  );
  return onSnapshot(q, (snap) => {
    const messages = snap.docs.map((d) => {
      const data = d.data() as any;
      return {
        id: d.id,
        text: data.text,
        authorId: data.authorId,
        authorName: data.authorName,
        timestamp: data.timestamp,
        editedAt: data.editedAt,
        html: data.html, // <— may be undefined
      };
    });
    callback(messages);
  });
}

export async function fetchTopicCount(forumId: string): Promise<number> {
  const snap = await getDocs(collection(db, "forums", forumId, "topics"));
  return snap.size;
}

/**
 * Return how many messages exist under this topic.
 */
export async function fetchMessageCount(
  forumId: string,
  topicId: string
): Promise<number> {
  const snap = await getDocs(
    collection(db, "forums", forumId, "topics", topicId, "messages")
  );
  return snap.size;
}

export async function postMessage1(
  forumId: string,
  topicId: string,
  text: string,
  authorId: string
): Promise<string> {
  const ref = await addDoc(
    collection(db, "forums", forumId, "topics", topicId, "messages"),
    {
      text,
      authorId,
      timestamp: serverTimestamp(),
    }
  );
  return ref.id;
}

export async function postMessage2(
  forumId: string,
  topicId: string,
  text: string,
  authorId: string
): Promise<string> {
  // 1) pull the user's profile
  const userSnap = await getDoc(doc(db, "users", authorId));
  const authorName = userSnap.exists()
    ? (userSnap.data() as any).displayName
    : "Anonymous";

  // 2) write both id + name into the message
  const ref = await addDoc(
    collection(db, "forums", forumId, "topics", topicId, "messages"),
    {
      text,
      authorId,
      authorName,
      timestamp: serverTimestamp(),
    }
  );
  return ref.id;
}

export async function postMessageLast(
  forumId: string,
  topicId: string,
  text: string,
  authorId: string
): Promise<string> {
  // 1) Load local profile for a human-readable name
  const profile = loadUserProfile();
  const authorName = profile.displayName || profile.username || "Anonymous";

  // 2) Write both id + name into the new message
  const ref = await addDoc(
    collection(db, "forums", forumId, "topics", topicId, "messages"),
    {
      text,
      authorId,
      authorName,
      timestamp: serverTimestamp(),
    }
  );
  return ref.id;
}

export async function postReply(
  forumId: string,
  topicId: string,
  parentId: string,
  text: string,
  authorId: string
): Promise<string> {
  const data = {
    text,
    parentId,
    authorId,
    authorName: loadUserProfile().displayName,
    avatarUrl: loadUserProfile().avatarUrl || null,
    timestamp: serverTimestamp(),
  };
  const ref = await addDoc(
    collection(db, "forums", forumId, "topics", topicId, "messages"),
    data
  );
  return ref.id;
}

export async function toggleStar(
  forumId: string,
  topicId: string,
  messageId: string,
  userId: string
): Promise<void> {
  const ref = doc(
    db,
    "forums",
    forumId,
    "topics",
    topicId,
    "messages",
    messageId
  );
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const stars = snap.data().stars || [];
  const hasStarred = stars.includes(userId);
  await updateDoc(ref, {
    stars: hasStarred ? arrayRemove(userId) : arrayUnion(userId),
  });
}

export async function getTopicTitle(
  forumId: string,
  topicId: string
): Promise<string | null> {
  const topicRef = doc(db, "forums", forumId, "topics", topicId);
  const topicSnap = await getDoc(topicRef);
  if (!topicSnap.exists()) return null;
  const data = topicSnap.data();
  return data.title || null;
}

export function subscribeMessagesWithReplies(
  forumId: string,
  topicId: string,
  callback: (messages: ForumMessage[]) => void
): Unsubscribe {
  const q = query(
    collection(db, "forums", forumId, "topics", topicId, "messages"),
    orderBy("timestamp")
  );

  return onSnapshot(q, (snapshot) => {
    const raw: ForumMessage[] = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as Omit<ForumMessage, "id" | "replies">),
      replies: [],
    }));

    const map = new Map<string, ForumMessage>();
    raw.forEach((msg) => map.set(msg.id, msg));

    const rootMessages: ForumMessage[] = [];
    raw.forEach((msg) => {
      if (msg.parentId) {
        const parent = map.get(msg.parentId);
        if (parent) parent.replies!.push(msg);
      } else {
        rootMessages.push(msg);
      }
    });

    callback(rootMessages);
  });
}

// Delete a forum
export async function deleteForum(forumId: string): Promise<void> {
  await deleteDoc(doc(db, "forums", forumId));
}

// Edit a forum's name or tags
export async function editForum(
  forumId: string,
  updates: { name?: string; tags?: string[] }
): Promise<void> {
  const forumRef = doc(db, "forums", forumId);
  await updateDoc(forumRef, updates);
}

/**
 *
 * postMessage: keep your authorName lookup, add optional html
 */
export async function postMessage(
  forumId: string,
  topicId: string,
  text: string,
  authorId: string,
  imgDataUrl?: string
): Promise<string> {
  // 1) Load local profile for a human-readable name
  const profile = loadUserProfile();
  const authorName = profile.displayName || profile.username || "Anonymous";

  // 2) Build payload
  const payload: any = {
    text,
    authorId,
    authorName,
    timestamp: serverTimestamp(),
  };

  // 3) If there's an attached image Data-URL, embed it as sanitized HTML
  if (imgDataUrl) {
    payload.html = `<img src="${imgDataUrl}" 
                           alt="attached" 
                           class="rounded border max-w-full" />`;
  }

  // 4) Persist
  const ref = await addDoc(
    collection(db, "forums", forumId, "topics", topicId, "messages"),
    payload
  );
  return ref.id;
}

export async function editMessage(
  forumId: string,
  topicId: string,
  msgId: string,
  newText: string
): Promise<void> {
  const msgRef = doc(
    db,
    "forums",
    forumId,
    "topics",
    topicId,
    "messages",
    msgId
  );
  await updateDoc(msgRef, {
    text: newText,
    editedAt: serverTimestamp(),
  });
}

export async function deleteMessage(
  forumId: string,
  topicId: string,
  msgId: string
): Promise<void> {
  await deleteDoc(
    doc(db, "forums", forumId, "topics", topicId, "messages", msgId)
  );
}
